import { Action } from 'redux';
import { AsyncStateSelector } from '@lib/selectors';
import { HttpStatusCode } from '@sonar-software/sonar-http';
import React from 'react';

import { ArgsProps, IconType } from 'antd/es/notification/interface';
import { AsyncStateStatus, isErrored, isFailed, isReceived, isRequested } from '@sonar-software/xuder';
import { StatusCodeHandlers, useAppNotifications, useAsyncProcessing } from '@hooks';

export interface NotificationOptions {
	description: string | React.ReactNode;
	options?: ArgsProps;
	type?: IconType;
}

export interface StatusCodeNotificationProps {
	options: NotificationOptions;
	statusCode: HttpStatusCode;
}

export interface AsyncStateNotificationProps {
	byStatusCode?: StatusCodeNotificationProps[];
	errored?: NotificationOptions | string;
	failed?: NotificationOptions | string;
	received?: NotificationOptions | string;
	requested?: NotificationOptions | string;
	resetAction?: () => void;
	resetRawAction?: () => Action;
	stateSelector: AsyncStateSelector<unknown>;
}

function getNotificationOptions(options: NotificationOptions | string | undefined): NotificationOptions | undefined {
	if (typeof options === 'string') {
		return {
			description: options
		};
	}

	return options;
}

function statusToAlertType(status: HttpStatusCode | AsyncStateStatus): IconType {
	const stateStatus = status as AsyncStateStatus;

	switch (true) {
	case isRequested(stateStatus):
		return 'info';
	case isErrored(stateStatus):
	case isFailed(stateStatus):
		return 'error';
	case isReceived(stateStatus):
		return 'success';
	default:
		return 'error';
	}
}

export const AsyncStateNotification: React.FC<AsyncStateNotificationProps> = ({
	byStatusCode,
	errored,
	failed,
	received,
	requested,
	resetRawAction,
	resetAction,
	stateSelector
}) => {
	const { openNotification } = useAppNotifications();

	useAsyncProcessing<NotificationOptions>({
		onReset: resetAction,
		onStatus:
			(status, options) => openNotification(statusToAlertType(status), options.description, options.options),
		onStatusCode:
			(status, options) => openNotification(statusToAlertType(status), options.description, options.options),
		resetAction: resetRawAction,
		stateSelector,
		statusCodeHandlers: byStatusCode?.reduce((codes, entry) => {
			codes[ entry.statusCode ] = entry.options;

			return codes;
		}, {} as StatusCodeHandlers<NotificationOptions>),
		statusHandlers: {
			errored: getNotificationOptions(errored),
			failed: getNotificationOptions(failed),
			received: getNotificationOptions(received),
			requested: getNotificationOptions(requested)
		},
		watchAllCalls: true
	});

	return null;
};
